import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';

interface PestDetail {
  id: string;
  name: string;
  scientificName: string;
  category: string;
  dangerLevel: 'Low' | 'Medium' | 'High';
  description: string;
  image: string;
  commonSigns: string[];
  habitat: string;
  seasonality: string;
  identification: string[];
  prevention: string[];
  treatment: string[];
  healthRisks: string[];
  lifecycle: string;
  diet: string;
  size: string;
}

@Component({
  selector: 'app-pest-detail',
  templateUrl: './pest-detail.component.html',
  styleUrl: './pest-detail.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MegamenuComponent,
    FooterComponent
  ]
})
export class PestDetailComponent implements OnInit {
  pest: PestDetail | null = null;

  private pestData: { [key: string]: PestDetail } = {
    'ants': {
      id: 'ants',
      name: 'Ants',
      scientificName: 'Formicidae',
      category: 'Insects',
      dangerLevel: 'Low',
      description: 'Social insects that live in colonies and can invade homes searching for food and water. Utah is home to several ant species including pavement ants, little black ants, and carpenter ants.',
      image: '/assets/Ant-Control-Sugar.jpeg',
      commonSigns: ['Ant trails leading to food sources', 'Small piles of dirt near entry points', 'Food contamination', 'Live ants in kitchen or pantry'],
      habitat: 'Outdoors in soil, under rocks, in wood. Indoors in kitchens, pantries, and near moisture sources',
      seasonality: 'Spring through Fall (March - October)',
      identification: [
        'Small insects, typically 1/8 to 1/2 inch long',
        'Three distinct body segments: head, thorax, abdomen',
        'Six legs and elbowed antennae',
        'Colors range from black to brown to red',
        'Often seen in trails following pheromone paths'
      ],
      prevention: [
        'Keep kitchen and dining areas clean',
        'Store food in airtight containers',
        'Wipe up spills immediately',
        'Seal cracks and entry points',
        'Remove standing water sources',
        'Trim vegetation away from house'
      ],
      treatment: [
        'Bait stations targeting the entire colony',
        'Perimeter spray treatments',
        'Crack and crevice applications',
        'Elimination of food and water sources',
        'Regular monitoring and maintenance'
      ],
      healthRisks: [
        'Generally low health risk',
        'Can contaminate food sources',
        'Some species can bite or sting',
        'May trigger allergic reactions in sensitive individuals'
      ],
      lifecycle: '6-10 weeks from egg to adult, depending on species and temperature',
      diet: 'Omnivorous - sugars, proteins, fats, and other organic matter',
      size: '1/8 to 1/2 inch long'
    },
    'spiders': {
      id: 'spiders',
      name: 'Spiders',
      scientificName: 'Araneae',
      category: 'Arachnids',
      dangerLevel: 'Medium',
      description: 'Eight-legged arachnids that help control other pests but can be unwelcome in homes. Utah has several spider species including house spiders, wolf spiders, and the venomous black widow.',
      image: '/assets/Spider-Control-Alive-Web.jpeg',
      commonSigns: ['Webs in corners and undisturbed areas', 'Egg sacs', 'Live spiders', 'Shed skins'],
      habitat: 'Dark corners, basements, garages, attics, outdoor vegetation, woodpiles',
      seasonality: 'Year-round, more active in warmer months (April - October)',
      identification: [
        'Eight legs and two main body segments',
        'No antennae or wings',
        'Size varies from tiny to several inches',
        'Colors range from brown to black to colorful patterns',
        'May have distinctive markings (like red hourglass on black widow)'
      ],
      prevention: [
        'Regular vacuuming of webs and spiders',
        'Reduce clutter in storage areas',
        'Seal cracks and gaps around windows and doors',
        'Remove outdoor debris and woodpiles',
        'Use yellow or sodium vapor lights outdoors',
        'Keep vegetation trimmed away from house'
      ],
      treatment: [
        'Web removal and spider elimination',
        'Perimeter treatments around foundation',
        'Crack and crevice applications',
        'Outdoor vegetation treatments',
        'Regular monitoring and maintenance'
      ],
      healthRisks: [
        'Most species are harmless to humans',
        'Black widow bites can be dangerous',
        'Bites may cause localized pain and swelling',
        'Seek medical attention for suspected venomous bites'
      ],
      lifecycle: '1-2 years depending on species, with multiple molts',
      diet: 'Carnivorous - insects, other spiders, small arthropods',
      size: '1/8 inch to 3+ inches including legs'
    },
    'cockroaches': {
      id: 'cockroaches',
      name: 'Cockroaches',
      scientificName: 'Blattodea',
      category: 'Insects',
      dangerLevel: 'High',
      description: 'Resilient insects that can spread diseases and contaminate food sources. Common Utah species include German cockroaches and American cockroaches.',
      image: '/assets/RoachOnBackInHome.jpeg',
      commonSigns: ['Dark droppings resembling coffee grounds', 'Musty, oily odor', 'Egg cases (oothecae)', 'Grease marks along walls', 'Live roaches at night'],
      habitat: 'Kitchens, bathrooms, basements, warm and humid areas, behind appliances',
      seasonality: 'Year-round, more active in warm conditions',
      identification: [
        'Oval, flattened body shape',
        'Long antennae',
        'Six spiny legs',
        'Brown to black coloration',
        'Size varies from 1/2 inch to 2+ inches',
        'Fast-moving when disturbed'
      ],
      prevention: [
        'Eliminate food and water sources',
        'Store food in sealed containers',
        'Fix leaky pipes and faucets',
        'Keep areas clean and dry',
        'Seal cracks and crevices',
        'Remove clutter and hiding places'
      ],
      treatment: [
        'Professional-grade baiting systems',
        'Crack and crevice treatments',
        'Growth regulators to disrupt reproduction',
        'Sanitation improvements',
        'Exclusion and sealing',
        'Regular monitoring'
      ],
      healthRisks: [
        'Can spread bacteria and pathogens',
        'Contaminate food and surfaces',
        'Trigger asthma and allergies',
        'Carry diseases like Salmonella and E. coli'
      ],
      lifecycle: '3-6 months from egg to adult, depending on conditions',
      diet: 'Omnivorous scavengers - food scraps, grease, organic matter',
      size: '1/2 inch to 2+ inches long'
    },
    'wasps': {
      id: 'wasps',
      name: 'Wasps',
      scientificName: 'Vespidae',
      category: 'Insects',
      dangerLevel: 'High',
      description: 'Aggressive stinging insects that can pose serious threats, especially to allergic individuals. Utah has several wasp species including yellowjackets and paper wasps.',
      image: '/assets/Wasp-Control-Alive-Group.jpeg',
      commonSigns: ['Visible nests under eaves or in trees', 'Increased wasp activity around food', 'Buzzing sounds', 'Aggressive behavior when disturbed'],
      habitat: 'Eaves, attics, trees, shrubs, ground burrows, wall voids',
      seasonality: 'Spring through Fall (April - October), peak activity in late summer',
      identification: [
        'Narrow waist between thorax and abdomen',
        'Bright yellow and black coloration',
        'Smooth, hairless body',
        'Long legs that dangle in flight',
        'Chewing mouthparts',
        'Can sting multiple times'
      ],
      prevention: [
        'Remove food sources and sugary drinks outdoors',
        'Keep garbage cans sealed',
        'Inspect and seal potential nesting sites',
        'Remove fallen fruit from trees',
        'Avoid wearing bright colors or floral patterns',
        'Stay calm and move slowly around wasps'
      ],
      treatment: [
        'Professional nest removal',
        'Protective equipment required',
        'Targeted insecticide applications',
        'Nest site treatment to prevent reestablishment',
        'Emergency removal services available'
      ],
      healthRisks: [
        'Painful stings that can cause swelling',
        'Multiple stings can be dangerous',
        'Life-threatening for allergic individuals',
        'Can trigger anaphylactic shock',
        'Seek immediate medical attention for severe reactions'
      ],
      lifecycle: 'Annual colonies, 4-6 months from spring to fall',
      diet: 'Adults feed on nectar and sugary substances, larvae eat insects',
      size: '1/2 to 1 inch long'
    },
    'rodents': {
      id: 'rodents',
      name: 'Rodents',
      scientificName: 'Rodentia',
      category: 'Rodents',
      dangerLevel: 'High',
      description: 'Mice and rats that can damage property and spread diseases through droppings and urine. Common Utah species include house mice, deer mice, and Norway rats.',
      image: '/assets/RatWithTechInBG.jpeg',
      commonSigns: ['Small, dark droppings', 'Gnaw marks on food packages', 'Scratching sounds in walls', 'Nests made of shredded materials', 'Grease marks along walls'],
      habitat: 'Attics, basements, wall voids, kitchens, garages, storage areas',
      seasonality: 'Year-round, more likely to enter homes in cooler months',
      identification: [
        'Small mammals with prominent front teeth',
        'Long, scaly tails',
        'Large ears and eyes',
        'Pointed snouts',
        'Size varies: mice 2-4 inches, rats 6-9 inches (excluding tail)',
        'Gray, brown, or black coloration'
      ],
      prevention: [
        'Seal holes and cracks larger than 1/4 inch',
        'Store food in rodent-proof containers',
        'Remove clutter and nesting materials',
        'Eliminate water sources',
        'Keep vegetation trimmed away from house',
        'Install door sweeps and weather stripping'
      ],
      treatment: [
        'Comprehensive inspection and sealing',
        'Strategic bait station placement',
        'Trapping programs',
        'Sanitation improvements',
        'Exclusion work',
        'Regular monitoring and maintenance'
      ],
      healthRisks: [
        'Can transmit diseases through droppings and urine',
        'Hantavirus risk from deer mice',
        'Salmonella and other bacterial infections',
        'Parasites like fleas and mites',
        'Contamination of food and surfaces'
      ],
      lifecycle: '9-12 weeks from birth to reproductive maturity',
      diet: 'Omnivorous - grains, seeds, fruits, insects, food scraps',
      size: 'Mice: 2-4 inches, Rats: 6-9 inches (body length)'
    },
    'mosquitoes': {
      id: 'mosquitoes',
      name: 'Mosquitoes',
      scientificName: 'Culicidae',
      category: 'Insects',
      dangerLevel: 'Medium',
      description: 'Blood-sucking insects that can transmit diseases and make outdoor activities unpleasant. Utah has several mosquito species that breed in standing water.',
      image: '/assets/Mosquito-Control-Alive.jpeg',
      commonSigns: ['Buzzing sounds around ears', 'Itchy bite marks on skin', 'Standing water with larvae', 'Increased activity at dawn and dusk'],
      habitat: 'Areas with standing water for breeding, tall grass, shaded outdoor areas',
      seasonality: 'Spring through Fall (April - October), peak activity in summer',
      identification: [
        'Small, slender insects 1/8 to 1/4 inch long',
        'Long, thin legs and antennae',
        'Needle-like mouthparts (proboscis)',
        'Narrow wings with scales',
        'Gray to black coloration',
        'High-pitched buzzing sound in flight'
      ],
      prevention: [
        'Eliminate standing water sources',
        'Clean gutters and drainage areas',
        'Change water in birdbaths weekly',
        'Use screens on windows and doors',
        'Wear long sleeves and pants outdoors',
        'Use EPA-approved insect repellents'
      ],
      treatment: [
        'Larvicide treatments for breeding sites',
        'Adult mosquito control sprays',
        'Yard and vegetation treatments',
        'Standing water management',
        'Seasonal treatment programs',
        'Integrated pest management approach'
      ],
      healthRisks: [
        'Can transmit West Nile virus',
        'Potential for other mosquito-borne diseases',
        'Allergic reactions to bites',
        'Secondary infections from scratching',
        'Disruption of outdoor activities'
      ],
      lifecycle: '7-14 days from egg to adult in warm weather',
      diet: 'Females feed on blood for egg production, males feed on nectar',
      size: '1/8 to 1/4 inch long'
    },
    'black-widow': {
      id: 'black-widow',
      name: 'Black Widow Spider',
      scientificName: 'Latrodectus hesperus',
      category: 'Arachnids',
      dangerLevel: 'High',
      description: 'Venomous spider with distinctive red hourglass marking, native to Utah. While bites are rare, they can be medically significant and require immediate attention.',
      image: '/assets/Black-Widow-Control-Closeup.jpeg',
      commonSigns: ['Irregular, messy webs', 'Distinctive red hourglass marking on female', 'Round egg sacs', 'Shy behavior, hiding in dark areas'],
      habitat: 'Dark, undisturbed areas like sheds, garages, woodpiles, rock piles, outdoor furniture',
      seasonality: 'Most active in warmer months (May - September)',
      identification: [
        'Females: shiny black with red hourglass on underside',
        'Males: smaller, brown with light markings (rarely seen)',
        'Body size: 1/2 inch (females), 1/4 inch (males)',
        'Bulbous abdomen on females',
        'Long, thin legs',
        'Irregular, sticky webs'
      ],
      prevention: [
        'Wear gloves when working in storage areas',
        'Shake out clothing and shoes before use',
        'Remove clutter and debris around property',
        'Seal cracks and crevices',
        'Use caution around woodpiles and outdoor equipment',
        'Regular cleaning of undisturbed areas'
      ],
      treatment: [
        'Professional identification and removal',
        'Targeted web and spider elimination',
        'Habitat modification',
        'Exclusion work around structures',
        'Regular monitoring of high-risk areas',
        'Emergency removal services'
      ],
      healthRisks: [
        'Venomous bite can cause serious symptoms',
        'Muscle pain and cramping',
        'Nausea and difficulty breathing',
        'Seek immediate medical attention for bites',
        'Antivenom available for severe cases'
      ],
      lifecycle: '1-3 years, with multiple egg sacs per female',
      diet: 'Insects caught in webs, including flies, mosquitoes, beetles',
      size: 'Females: 1/2 inch body, Males: 1/4 inch body'
    },
    'crickets': {
      id: 'crickets',
      name: 'Crickets',
      scientificName: 'Gryllidae',
      category: 'Insects',
      dangerLevel: 'Low',
      description: 'Chirping insects that can become nuisances when they invade homes in large numbers. Utah has several cricket species including house crickets and field crickets.',
      image: '/assets/Cricket-Control-Dead.jpeg',
      commonSigns: ['Loud chirping sounds, especially at night', 'Damage to fabrics and paper', 'Live insects hopping around', 'Small holes in clothing'],
      habitat: 'Basements, crawl spaces, outdoor vegetation, under rocks and logs',
      seasonality: 'Late summer through fall (August - October)',
      identification: [
        'Brown to black coloration',
        'Long antennae',
        'Powerful hind legs for jumping',
        'Flattened body shape',
        'Size: 1/2 to 1 inch long',
        'Males produce chirping sounds'
      ],
      prevention: [
        'Seal cracks and entry points',
        'Reduce outdoor lighting that attracts crickets',
        'Remove debris and vegetation near foundation',
        'Fix moisture problems',
        'Use weather stripping around doors',
        'Keep grass and weeds trimmed'
      ],
      treatment: [
        'Perimeter treatments around foundation',
        'Crack and crevice applications',
        'Habitat modification',
        'Exclusion work',
        'Outdoor vegetation treatments',
        'Moisture control'
      ],
      healthRisks: [
        'Generally harmless to humans',
        'Can damage fabrics and paper products',
        'Noise disturbance from chirping',
        'May attract other pests that feed on them'
      ],
      lifecycle: '2-3 months from egg to adult',
      diet: 'Omnivorous - plant matter, fabrics, paper, other insects',
      size: '1/2 to 1 inch long'
    }
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      const pestId = params['id'];
      this.pest = this.pestData[pestId] || null;
      
      if (!this.pest) {
        this.router.navigate(['/pest-library']);
      }
    });
  }

  getDangerColor(level: string): string {
    switch (level) {
      case 'High': return '#dc3545';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  }

  goBack() {
    this.router.navigate(['/pest-library']);
  }
}